// Drag interaction state lives in refs written during render, which React Compiler cannot memoize safely.
"use no memo";
// oxlint-disable react/react-compiler -- opted out of React Compiler above

import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  defaultChatModel,
  MANAGED_AI_PROVIDER_ID,
  resolveManagedChatModel,
} from "../../../shared/aiProviders";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { buildModelPickerItems } from "../modelPickerItems";
import { useQuery } from "../../hooks/useQuery";
import { useScopedModel } from "../../hooks/useScopedModel";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { useSpaces } from "../../hooks/useSpaces";
import { requestChatBrowserNotificationPermission } from "../../lib/browserNotifications";
import { formatModelName } from "../../lib/formatModelName";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { formatDateShort } from "../../lib/formatTimestamp";
import { resolveDefaultPlanMode } from "../../lib/planMode";
import { sortPersonalFirst } from "../../lib/sortSpaces";
import { playClick } from "../../lib/sounds";
import { spaceIcon } from "../../lib/spaceIcon";
import { navigate, routes } from "../../lib/urls";
import { Box } from "../Box";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { Dropdown } from "../Dropdown";
import { Icon, type IconName } from "../Icon";
import { PressButton } from "../PressButton";
import { ProviderLogo } from "../ProviderLogo";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import {
  DEFAULT_TAB_ORDER,
  resolveWorkflowTabOrder,
  type Tab,
  type TabDef,
} from "../WorkflowTabs";
import { DropdownOpener, SectionHeading } from "./shared";

const Container = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Card = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;
  gap: 24px;
  padding: 64px 24px;
  overflow: auto;
  box-shadow: var(--elevation-low);
  background: var(--surface-primary);
  border-radius: 16px;
  outline: 0.5px solid var(--border-alpha);
`;

const Header = styled("div")`
  width: 100%;
  max-width: 648px;
  padding-left: 24px;
  margin-bottom: 32px;
`;

const ContentBox = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 648px;
  padding: 24px;
  background: var(--surface-secondary);
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
`;

const Section = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled("div")`
  display: flex;
  flex-direction: column;
`;

const ToggleRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ToggleGroup = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background-color: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
`;

const TabOrderHelperRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 8px;
  gap: 12px;
`;

const TabOrderResetLink = styled(PressButton)`
  cursor: pointer;
  font-weight: 350;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: -1%;
  color: var(--text-primary);
  opacity: 0.5;
  white-space: nowrap;
  &:hover {
    opacity: 1;
  }
`;

const TabPillContainer = styled("div")`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: var(--surface-primary);
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
`;

const TabPill = styled("div")`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  background: var(--alpha4);
  border-radius: 999px;
  color: var(--text-secondary);
  cursor: grab;
  user-select: none;
  white-space: nowrap;
  touch-action: none;

  &:hover {
    background: var(--alpha8);
    color: var(--text-primary);
  }

  &[data-ghost="true"] {
    background: var(--alpha2);
    color: transparent;
  }

  &[data-floating="true"] {
    cursor: grabbing;
    background: var(--surface-primary);
    color: var(--text-primary);
    outline: 0.5px solid var(--border-alpha);
    box-shadow: var(--elevation-low);
    transform: scale(1.05) rotate(-2deg);
    transition:
      box-shadow 0.6s cubic-bezier(0.2, 0, 0, 1),
      transform 0.6s cubic-bezier(0.2, 0, 0, 1);
  }

  &[data-floating="true"][data-resting="true"] {
    transform: none;
    box-shadow: none;
  }
`;

function WorkflowTabReorder({
  saved,
  onChange,
}: {
  saved: string | null | undefined;
  onChange: (next: string) => void;
}) {
  const ordered = resolveWorkflowTabOrder(saved);
  const [draggedKey, setDraggedKey] = useState<Tab | null>(null);
  const [dropIndex, setDropIndex] = useState(-1);
  const [dropping, setDropping] = useState(false);
  const [lifting, setLifting] = useState(false);

  const pillRefs = useRef<Map<Tab, HTMLElement>>(new Map());
  const dragStart = useRef({ x: 0, y: 0 });
  const originRect = useRef<DOMRect | null>(null);
  const pillCenters = useRef<{ x: number; y: number; index: number }[]>([]);
  const floatingRef = useRef<HTMLDivElement>(null);
  const draggedKeyRef = useRef<Tab | null>(null);
  const dropIndexRef = useRef(-1);
  const initialDropIdx = useRef(-1);
  const flipSnapshotsRef = useRef<Map<Tab, DOMRect>>(new Map());

  const setPillRef = useCallback((key: Tab, el: HTMLElement | null) => {
    if (el) pillRefs.current.set(key, el);
    else pillRefs.current.delete(key);
  }, []);

  const orderedRef = useRef(ordered);
  orderedRef.current = ordered;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handlePointerDown = useCallback((e: React.PointerEvent, key: Tab) => {
    if (e.button !== 0) return;
    const current = orderedRef.current;
    const el = pillRefs.current.get(key);
    if (!el) return;

    playClick();
    dragStart.current = { x: e.clientX, y: e.clientY };
    originRect.current = el.getBoundingClientRect();

    pillCenters.current = current
      .filter((t) => t.key !== key)
      .map((t, i) => {
        const ref = pillRefs.current.get(t.key);
        if (!ref) return { x: 0, y: 0, index: i };
        const rect = ref.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
          index: i,
        };
      });

    const idx = current.findIndex((t) => t.key === key);
    draggedKeyRef.current = key;
    dropIndexRef.current = idx;
    initialDropIdx.current = idx;
    setDropIndex(idx);
    setLifting(true);
    setDraggedKey(key);
    requestAnimationFrame(() => setLifting(false));
  }, []);

  useEffect(() => {
    if (!draggedKey) return;

    const onMove = (e: PointerEvent) => {
      if (!draggedKeyRef.current || !originRect.current) return;

      if (floatingRef.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        floatingRef.current.style.left = `${originRect.current.left + dx}px`;
        floatingRef.current.style.top = `${originRect.current.top + dy}px`;
      }

      if (pillCenters.current.length === 0) return;

      const cx = e.clientX;
      let nearestI = pillCenters.current[0]!.index;
      let nearestDist = Number.POSITIVE_INFINITY;
      let nearestCenter = pillCenters.current[0]!;
      for (const c of pillCenters.current) {
        const ddx = cx - c.x;
        const ddy = e.clientY - c.y;
        const d = ddx * ddx + ddy * ddy;
        if (d < nearestDist) {
          nearestDist = d;
          nearestI = c.index;
          nearestCenter = c;
        }
      }
      let idx = nearestI;
      if (cx > nearestCenter.x) idx = nearestI + 1;
      const maxIdx = orderedRef.current.length - 1;
      if (idx > maxIdx) idx = maxIdx;
      if (idx < 0) idx = 0;

      if (idx !== dropIndexRef.current) {
        const snapshots = new Map<Tab, DOMRect>();
        for (const [k, el] of pillRefs.current) {
          snapshots.set(k, el.getBoundingClientRect());
        }
        flipSnapshotsRef.current = snapshots;
        dropIndexRef.current = idx;
        setDropIndex(idx);
        playClick();
      }
    };

    const onUp = () => {
      if (!draggedKeyRef.current) return;
      const draggedEl = pillRefs.current.get(draggedKeyRef.current);
      const el = floatingRef.current;
      if (draggedEl && el) {
        const rect = draggedEl.getBoundingClientRect();
        el.getBoundingClientRect();
        el.style.transition = "left 0.2s ease, top 0.2s ease";
        el.style.left = `${rect.left}px`;
        el.style.top = `${rect.top}px`;
      }
      draggedKeyRef.current = null;
      setDropping(true);
    };

    const onCancel = () => {
      if (!draggedKeyRef.current) return;
      draggedKeyRef.current = null;
      originRect.current = null;
      dropIndexRef.current = -1;
      initialDropIdx.current = -1;
      flipSnapshotsRef.current.clear();
      setDraggedKey(null);
      setDropIndex(-1);
      setDropping(false);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [draggedKey]);

  useEffect(() => {
    if (!dropping) return;
    const timer = setTimeout(() => {
      if (draggedKey && dropIndex !== initialDropIdx.current) {
        const current = orderedRef.current;
        const dragged = current.find((t) => t.key === draggedKey);
        if (dragged) {
          const rest = current.filter((t) => t.key !== draggedKey);
          rest.splice(dropIndex, 0, dragged);
          onChangeRef.current(rest.map((t) => t.key).join(","));
        }
      }
      playClick();
      originRect.current = null;
      dropIndexRef.current = -1;
      initialDropIdx.current = -1;
      setDraggedKey(null);
      setDropIndex(-1);
      setDropping(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [dropping, draggedKey, dropIndex]);

  const visualOrder = useTabReorderPreview(ordered, draggedKey, dropIndex);
  useTabFlipAnimation(visualOrder, pillRefs, flipSnapshotsRef, !!draggedKey);

  const draggedTab = draggedKey
    ? ordered.find((t) => t.key === draggedKey)
    : null;

  return (
    <TabPillContainer>
      {visualOrder.map((t) => {
        const isDragged = t.key === draggedKey;
        return (
          <TabPill
            key={t.key}
            ref={(el) => setPillRef(t.key, el)}
            data-ghost={isDragged ? "true" : undefined}
            onPointerDown={(e) => handlePointerDown(e, t.key)}
            style={{ cursor: draggedKey ? "grabbing" : undefined }}
          >
            <Icon name={t.icon} size={12} />
            <Text variant="text-small-md">{t.label}</Text>
          </TabPill>
        );
      })}

      {draggedTab && originRect.current && (
        <div
          ref={floatingRef}
          style={{
            position: "fixed",
            left: originRect.current.left,
            top: originRect.current.top,
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          <TabPill
            data-floating="true"
            data-resting={lifting || dropping ? "true" : undefined}
          >
            <Icon name={draggedTab.icon} size={12} />
            <Text variant="text-small-md">{draggedTab.label}</Text>
          </TabPill>
        </div>
      )}
    </TabPillContainer>
  );
}

function useTabReorderPreview(
  ordered: ReadonlyArray<TabDef>,
  draggedKey: Tab | null,
  dropIndex: number
): ReadonlyArray<TabDef> {
  if (!draggedKey || dropIndex < 0) return ordered;
  const dragged = ordered.find((t) => t.key === draggedKey);
  if (!dragged) return ordered;
  const rest = ordered.filter((t) => t.key !== draggedKey);
  const clamped = Math.min(Math.max(0, dropIndex), rest.length);
  const next = rest.slice();
  next.splice(clamped, 0, dragged);
  return next;
}

function useTabFlipAnimation(
  order: ReadonlyArray<TabDef>,
  pillRefs: React.MutableRefObject<Map<Tab, HTMLElement>>,
  snapshotsRef: React.MutableRefObject<Map<Tab, DOMRect>>,
  draggingActive: boolean
) {
  useLayoutEffect(() => {
    if (!draggingActive) {
      for (const el of pillRefs.current.values()) {
        el.style.transform = "";
        el.style.transition = "";
      }
      snapshotsRef.current.clear();
      return;
    }

    const snapshots = snapshotsRef.current;
    if (snapshots.size === 0) return;

    for (const t of order) {
      const el = pillRefs.current.get(t.key);
      if (!el) continue;
      const prev = snapshots.get(t.key);
      if (!prev) continue;

      el.style.transition = "none";
      el.style.transform = "";
      const layout = el.getBoundingClientRect();
      const dx = prev.left - layout.left;
      const dy = prev.top - layout.top;
      if (dx === 0 && dy === 0) continue;

      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.getBoundingClientRect();
      el.style.transition = "transform 200ms ease";
      el.style.transform = "";
    }

    snapshots.clear();
  }, [order, pillRefs, snapshotsRef, draggingActive]);
}

const homeOptions: { value: string; label: string; icon: IconName }[] = [
  { value: "recents", label: "Recents", icon: "history" },
  { value: "chats", label: "Chat", icon: "ask" },
  { value: "favorites", label: "Favorites", icon: "heart" },
];

const followUpOptions: {
  value: "queue" | "steer";
  label: string;
  icon: IconName;
}[] = [
  { value: "queue", label: "Queue", icon: "list-ordered" },
  { value: "steer", label: "Steer", icon: "joystick" },
];

const IconTabContainer = styled("div")`
  display: flex;
  align-items: center;
  align-self: flex-start;
  height: 32px;
  background: var(--alpha4);
  border-radius: 8px;
  padding: 0 2px;
  position: relative;
`;

const IconTabIndicator = styled("div")`
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  outline: 0.5px solid var(--border-alpha);
  transition: transform 0.15s ease;
  pointer-events: none;
`;

const IconTab = styled(PressButton)`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  background: transparent;
  color: var(--text-secondary);
  opacity: 0.5;
  position: relative;
  z-index: 1;
  &:hover {
    color: var(--text-primary);
    opacity: 1;
  }
  &[data-active="true"] {
    color: var(--text-primary);
    opacity: 1;
  }
`;

export function PreferencesSettings() {
  const z = useZero();
  const [preferences] = useQuery(queries.userPreferences());
  const [oauthGrants] = useQuery(queries.myOAuthGrants());
  const [revokeGrantId, setRevokeGrantId] = useState<string | null>(null);
  const displayName = useSpaceDisplayName();
  const [spaces] = useSpaces({ settingsView: false });

  const [personalSpace] = useQuery(queries.personalSpace());
  const personalSpaceId = personalSpace?.id ?? null;
  const sortedSpaces = sortPersonalFirst(spaces, personalSpaceId);
  const defaultHome = preferences?.defaultHome ?? "recents";
  const defaultSpace = preferences?.defaultSpace ?? null;
  const effectiveDefaultSpace = defaultSpace ?? personalSpaceId;
  const followUpBehavior =
    preferences?.followUpBehavior === "steer" ? "steer" : "queue";
  const soundClicks = preferences?.soundClicks !== "off";
  const soundChat = preferences?.soundChat !== "off";
  const soundWorkflow = preferences?.soundWorkflow !== "off";
  const browserNotifications = preferences?.browserNotifications === "on";
  const follow3bByDefault = preferences?.follow3bByDefault !== "off";
  const suggestFollow3b = preferences?.suggestFollow3b !== "off";
  const planModeWorkflowDefault = resolveDefaultPlanMode(
    preferences,
    "workflow"
  );
  const planModeBranchDefault = resolveDefaultPlanMode(preferences, "branch");
  const theme = (preferences?.theme as "light" | "dark" | "system") ?? "system";

  const setPreference = (
    updates: Parameters<
      (typeof sharedMutators)["setUserPreference"]["fn"]
    >[0]["args"]
  ) => {
    z.mutate(sharedMutators.setUserPreference(updates));
  };

  const modelScoped = useScopedModel(
    { type: "personal" },
    { chatModel: preferences?.chatModel ?? null }
  );
  const selectedManagedModel = modelScoped.managedProvider
    ? resolveManagedChatModel(preferences?.chatModel)
    : null;
  const selectedModelId =
    modelScoped.effectiveConfig?.chatModel ??
    (modelScoped.effectiveKey === MANAGED_AI_PROVIDER_ID && selectedManagedModel
      ? selectedManagedModel
      : defaultChatModel(modelScoped.providerId));
  const modelItems = buildModelPickerItems({
    managedProvider: modelScoped.managedProvider,
    configs: modelScoped.configs,
    tenantDefaultKey: modelScoped.tenantDefaultKey,
    effectiveKey: modelScoped.effectiveKey,
    managedChatModel: selectedManagedModel,
    onSelectManaged: (modelId) =>
      setPreference({
        aiProviderId: MANAGED_AI_PROVIDER_ID,
        chatModel: modelId,
      }),
    onSelectConfig: (configId) =>
      setPreference({ aiProviderId: configId, chatModel: null }),
  });
  const hasModelPreference =
    preferences?.aiProviderId != null || preferences?.chatModel != null;

  return (
    <Container>
      <Card>
        <Header>
          <Text variant="display-md">Preferences</Text>
        </Header>

        <ContentBox>
          <SectionHeading>
            <Text variant="text-regular-md">General</Text>
          </SectionHeading>
          <Section>
            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Default home view</Text>
              </Box>
              <Dropdown
                opener={
                  <DropdownOpener style={{ width: 208 }}>
                    <Text variant="text-regular" ellipsis>
                      {homeOptions.find((o) => o.value === defaultHome)
                        ?.label ??
                        (() => {
                          const s = spaces.find((s) => s.id === defaultHome);
                          return s ? displayName(s) : "Recents";
                        })()}
                    </Text>
                    <span style={{ opacity: 0.3 }}>
                      <Icon name="chevron-down" size={12} />
                    </span>
                  </DropdownOpener>
                }
                align="bottom-left"
                sections={[
                  {
                    items: homeOptions.map((opt) => ({
                      type: "button" as const,
                      left: <Icon name={opt.icon} size={16} />,
                      label: opt.label,
                      right:
                        opt.value === defaultHome ? (
                          <Icon
                            name="check"
                            size={12}
                            color="var(--text-secondary)"
                          />
                        ) : undefined,
                      onClick: () => setPreference({ defaultHome: opt.value }),
                    })),
                  },
                  {
                    label: "Spaces",
                    items: sortedSpaces.map((space) => ({
                      type: "button" as const,
                      left: <Icon name={spaceIcon(space.mode)} size={16} />,
                      label: displayName(space),
                      right:
                        space.id === defaultHome ? (
                          <Icon
                            name="check"
                            size={12}
                            color="var(--text-secondary)"
                          />
                        ) : undefined,
                      onClick: () => setPreference({ defaultHome: space.id }),
                    })),
                  },
                ]}
              />
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  Choose which view to show when you open the app
                </Text>
              </Box>
            </Field>

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Default space</Text>
              </Box>
              <Dropdown
                opener={
                  <DropdownOpener style={{ width: 208 }}>
                    <Text variant="text-regular" ellipsis>
                      {(() => {
                        const s = spaces.find(
                          (s) => s.id === effectiveDefaultSpace
                        );
                        return s ? displayName(s) : "Personal workflows";
                      })()}
                    </Text>
                    <span style={{ opacity: 0.3 }}>
                      <Icon name="chevron-down" size={12} />
                    </span>
                  </DropdownOpener>
                }
                align="bottom-left"
                sections={[
                  {
                    items: sortedSpaces.map((space) => ({
                      type: "button" as const,
                      left: <Icon name={spaceIcon(space.mode)} size={16} />,
                      label: displayName(space),
                      right:
                        space.id === effectiveDefaultSpace ? (
                          <Icon
                            name="check"
                            size={12}
                            color="var(--text-secondary)"
                          />
                        ) : undefined,
                      onClick: () => setPreference({ defaultSpace: space.id }),
                    })),
                  },
                ]}
              />
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  New workflows created from recents will be added to this space
                </Text>
              </Box>
            </Field>

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Sidebar</Text>
              </Box>
              <DropdownOpener
                style={{ width: 208 }}
                onClick={() => {
                  navigate(routes.home);
                  navigate(routes.modal.customizeNav);
                }}
              >
                <Text variant="text-regular">Customize sidebar</Text>
                <span style={{ opacity: 0.3 }}>
                  <Icon name="arrow-up-right" size={12} />
                </span>
              </DropdownOpener>
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  Reorder items and choose when each appears in your sidebar
                </Text>
              </Box>
            </Field>

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Default workflow tab order</Text>
              </Box>
              <WorkflowTabReorder
                saved={preferences?.workflowTabOrder}
                onChange={(next) => setPreference({ workflowTabOrder: next })}
              />
              <TabOrderHelperRow>
                <Text variant="text-small" color="var(--text-secondary)">
                  Drag to set the order tabs appear at the top of every workflow
                </Text>
                {preferences?.workflowTabOrder &&
                  preferences.workflowTabOrder !== DEFAULT_TAB_ORDER && (
                    <TabOrderResetLink
                      onPress={() => setPreference({ workflowTabOrder: null })}
                    >
                      Reset
                    </TabOrderResetLink>
                  )}
              </TabOrderHelperRow>
            </Field>
          </Section>
        </ContentBox>
        <ContentBox>
          <SectionHeading>
            <Text variant="text-regular-md">Chat</Text>
          </SectionHeading>
          <Section>
            {modelItems.length > 0 && (
              <Field>
                <Box horizontal={8} vertical={10}>
                  <Text variant="text-small-md">Preferred model</Text>
                </Box>
                <Dropdown
                  opener={
                    <DropdownOpener style={{ width: 208 }}>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          minWidth: 0,
                        }}
                      >
                        <ProviderLogo
                          provider={modelScoped.providerId}
                          size={16}
                        />
                        <Text variant="text-regular" ellipsis>
                          {formatModelName(selectedModelId)}
                        </Text>
                      </span>
                      <span style={{ opacity: 0.3 }}>
                        <Icon name="chevron-down" size={12} />
                      </span>
                    </DropdownOpener>
                  }
                  align="bottom-left"
                  sections={[
                    { items: modelItems },
                    ...(hasModelPreference
                      ? [
                          {
                            items: [
                              {
                                key: "use-default",
                                type: "button" as const,
                                left: null,
                                label: "Use default model",
                                onClick: () =>
                                  setPreference({
                                    aiProviderId: null,
                                    chatModel: null,
                                  }),
                              },
                            ],
                          },
                        ]
                      : []),
                  ]}
                />
                <Box horizontal={8} top={6}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    The model your chats use by default. You can still switch
                    models in any chat.
                  </Text>
                </Box>
              </Field>
            )}

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Chat follow-up behavior</Text>
              </Box>
              <Dropdown
                opener={
                  <DropdownOpener style={{ width: 208 }}>
                    <Text variant="text-regular">
                      {followUpBehavior === "steer" ? "Steer" : "Queue"}
                    </Text>
                    <span style={{ opacity: 0.3 }}>
                      <Icon name="chevron-down" size={12} />
                    </span>
                  </DropdownOpener>
                }
                align="bottom-left"
                sections={[
                  {
                    items: followUpOptions.map((opt) => ({
                      type: "button" as const,
                      left: <Icon name={opt.icon} size={16} />,
                      label: opt.label,
                      right:
                        opt.value === followUpBehavior ? (
                          <Icon
                            name="check"
                            size={12}
                            color="var(--text-secondary)"
                          />
                        ) : undefined,
                      onClick: () =>
                        setPreference({ followUpBehavior: opt.value }),
                    })),
                  },
                ]}
              />
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  Queue messages to send after the agent finishes, or steer the
                  agent mid-turn. Use ⌘↵ to{" "}
                  {followUpBehavior === "steer" ? "queue" : "steer"}
                </Text>
              </Box>
            </Field>

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Plan mode</Text>
              </Box>
              <ToggleGroup>
                <ToggleRow>
                  <Text variant="text-regular">
                    New workflows start in plan mode
                  </Text>
                  <Toggle
                    isOn={planModeWorkflowDefault}
                    onToggle={() =>
                      setPreference({
                        planModeWorkflowDefault: planModeWorkflowDefault
                          ? "off"
                          : "on",
                      })
                    }
                  />
                </ToggleRow>
                <ToggleRow>
                  <Text variant="text-regular">
                    New branches start in plan mode
                  </Text>
                  <Toggle
                    isOn={planModeBranchDefault}
                    onToggle={() =>
                      setPreference({
                        planModeBranchDefault: planModeBranchDefault
                          ? "off"
                          : "on",
                      })
                    }
                  />
                </ToggleRow>
              </ToggleGroup>
            </Field>

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Follow 3B</Text>
              </Box>
              <ToggleGroup>
                <ToggleRow>
                  <Text variant="text-regular">Follow 3B by default</Text>
                  <Toggle
                    isOn={follow3bByDefault}
                    onToggle={() =>
                      setPreference({
                        follow3bByDefault: follow3bByDefault ? "off" : "on",
                      })
                    }
                  />
                </ToggleRow>
                <ToggleRow>
                  <Text variant="text-regular">
                    Suggest following on a new chat
                  </Text>
                  <Toggle
                    isOn={suggestFollow3b}
                    onToggle={() =>
                      setPreference({
                        suggestFollow3b: suggestFollow3b ? "off" : "on",
                      })
                    }
                  />
                </ToggleRow>
              </ToggleGroup>
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  When you’re following, the workflow view jumps to the step 3B
                  is editing.
                </Text>
              </Box>
            </Field>
          </Section>
        </ContentBox>
        <ContentBox>
          <SectionHeading>
            <Text variant="text-regular-md">Sounds and notifications</Text>
          </SectionHeading>
          <Section>
            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Sounds</Text>
              </Box>
              <ToggleGroup>
                <ToggleRow>
                  <Text variant="text-regular">Chat sounds</Text>
                  <Toggle
                    isOn={soundChat}
                    onToggle={() =>
                      setPreference({ soundChat: soundChat ? "off" : "on" })
                    }
                  />
                </ToggleRow>
                <ToggleRow>
                  <Text variant="text-regular">UI clicks</Text>
                  <Toggle
                    isOn={soundClicks}
                    onToggle={() =>
                      setPreference({
                        soundClicks: soundClicks ? "off" : "on",
                      })
                    }
                  />
                </ToggleRow>
                <ToggleRow>
                  <Text variant="text-regular">Workflow sounds</Text>
                  <Toggle
                    isOn={soundWorkflow}
                    onToggle={() =>
                      setPreference({
                        soundWorkflow: soundWorkflow ? "off" : "on",
                      })
                    }
                  />
                </ToggleRow>
              </ToggleGroup>
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  Control which sounds play throughout the app
                </Text>
              </Box>
            </Field>

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Browser notifications</Text>
              </Box>
              <ToggleGroup>
                <ToggleRow>
                  <Text variant="text-regular">
                    Notify when 3B is finished working or needs input in a
                    background browser tab
                  </Text>
                  <Toggle
                    isOn={browserNotifications}
                    onToggle={async () => {
                      const next = browserNotifications ? "off" : "on";
                      if (next === "on") {
                        const permission =
                          await requestChatBrowserNotificationPermission();
                        if (permission !== "granted") return;
                      }
                      setPreference({ browserNotifications: next });
                    }}
                  />
                </ToggleRow>
              </ToggleGroup>
            </Field>
          </Section>
        </ContentBox>
        <ContentBox>
          <SectionHeading>
            <Text variant="text-regular-md">Appearance</Text>
          </SectionHeading>
          <Section>
            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Theme</Text>
              </Box>
              <IconTabContainer>
                <IconTabIndicator
                  style={{
                    transform: `translateX(${
                      theme === "system" ? 0 : theme === "light" ? 28 : 56
                    }px)`,
                  }}
                />
                {(
                  [
                    { value: "system", icon: "monitor-smartphone" },
                    { value: "light", icon: "sun" },
                    { value: "dark", icon: "moon" },
                  ] as const
                ).map((opt) => (
                  <IconTab
                    key={opt.value}
                    data-active={theme === opt.value}
                    onPress={() => setPreference({ theme: opt.value })}
                  >
                    <Icon name={opt.icon} size={16} />
                  </IconTab>
                ))}
              </IconTabContainer>
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  Choose between light, dark, or system theme
                </Text>
              </Box>
            </Field>
          </Section>
        </ContentBox>
        {oauthGrants.length > 0 && (
          <ContentBox>
            <SectionHeading>
              <Text variant="text-regular-md">Connected accounts</Text>
            </SectionHeading>
            <Section>
              <Field>
                <Box horizontal={8} vertical={10}>
                  <Text variant="text-small-md">OAuth apps</Text>
                </Box>
                <ToggleGroup>
                  {oauthGrants.map((grant) => (
                    <ToggleRow key={grant.id}>
                      <Box gap={2}>
                        <Text variant="text-regular">
                          {grant.client?.name ?? grant.clientId}
                        </Text>
                        {grant.client?.clientIdUrl && (
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                          >
                            {grant.client.clientIdUrl}
                          </Text>
                        )}
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                        >
                          {[
                            grant.createdAt &&
                              `Authorized ${formatDateShort(grant.createdAt)}`,
                            grant.lastUsedAt
                              ? `Last used ${formatRelativeTime(grant.lastUsedAt)}`
                              : "Never used",
                            grant.scopes.join(", "),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Text>
                      </Box>
                      <Button
                        size="m"
                        variant="secondary"
                        onClick={() => setRevokeGrantId(grant.id)}
                      >
                        Revoke
                      </Button>
                    </ToggleRow>
                  ))}
                </ToggleGroup>
                <Box horizontal={8} top={6}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Apps you’ve authorized via OAuth that can act as you. Revoke
                    to cut off their access immediately.
                  </Text>
                </Box>
              </Field>
            </Section>
          </ContentBox>
        )}
      </Card>
      {revokeGrantId && (
        <ConfirmModal
          open
          onClose={() => setRevokeGrantId(null)}
          onConfirm={() => {
            z.mutate(sharedMutators.revokeOAuthGrant({ id: revokeGrantId }));
            setRevokeGrantId(null);
          }}
          title="Revoke app access"
          description="This app will lose access immediately, and any of its active tokens acting as you stop working. This can’t be undone."
          confirmLabel="Revoke"
          destructive
        />
      )}
    </Container>
  );
}
